import { ChangeEvent, FormEvent, useEffect, useRef, useState } from "react";

import { Ds, Feedback, VisuallyHidden } from "@accurx/design";

import { ExpandMinimiseBar } from "../ConversationView/components/ComposeNote/components/ExpandMinimiseBar/ExpandMinimiseBar";
import { MAX_NOTE_LENGTH, validateFormState } from "./NoteForm.helpers";
import {
    FormWrapper,
    IconWrapper,
    StyledForm,
    StyledTextArea,
    TextAreaWrapper,
} from "./NoteForm.styles";

type NoteFormProps = {
    value: string;
    onChange: (note: string) => void;
    onSubmit: (note: string) => void;
    onExpand?: () => void;
    onMinimise?: () => void;
    isLoading?: boolean;
    isHeightRestricted?: boolean;
};

export const NoteForm = ({
    value,
    onChange,
    onSubmit,
    onExpand,
    onMinimise,
    isLoading = false,
    isHeightRestricted = false,
}: NoteFormProps) => {
    const [error, setError] = useState("");
    const ref = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        ref.current?.focus();
    }, []);

    const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
        const value = event.currentTarget.value;
        onChange(value);
        setError("");
    };

    const handleSubmit = (event: FormEvent) => {
        event.preventDefault();
        const error = validateFormState(value);

        if (error) {
            setError(error);
            ref.current?.focus();
            return;
        }

        onSubmit(value);
    };

    return (
        <StyledForm onSubmit={handleSubmit}>
            <FormWrapper flexDirection="column" gap="1.5">
                {(onExpand || onMinimise) && (
                    <ExpandMinimiseBar
                        onMinimiseClick={onMinimise}
                        onExpandClick={onExpand}
                        isHeightRestricted={isHeightRestricted}
                    />
                )}
                {error && (
                    <Ds.Flex.Item flex="0">
                        <Feedback
                            title={error}
                            colour="error"
                            iconName="Failed"
                        />
                    </Ds.Flex.Item>
                )}
                <VisuallyHidden as="span">
                    <label htmlFor="add-note">
                        Write your note here. It's only visible to colleagues in
                        your workspace
                    </label>
                </VisuallyHidden>
                <TextAreaWrapper>
                    <StyledTextArea
                        disabled={isLoading}
                        placeholder="Write your note here..."
                        onChange={handleChange}
                        maxLength={MAX_NOTE_LENGTH}
                        value={value}
                        ref={ref}
                        id="add-note"
                    />
                </TextAreaWrapper>
                <Ds.Flex justifyContent="space-between">
                    <Ds.Flex alignItems="center">
                        <IconWrapper>
                            <Ds.Icon
                                name="LockLocked"
                                size="xsmall"
                                color="metal"
                                appearance="solid"
                            />
                        </IconWrapper>
                        <Ds.Text size="small" color="metal">
                            Only visible to colleagues in your workspace
                        </Ds.Text>
                    </Ds.Flex>
                    <Ds.Button
                        type="submit"
                        size="small"
                        loading={isLoading}
                        loadingMessage="Adding..."
                    >
                        Add note
                    </Ds.Button>
                </Ds.Flex>
            </FormWrapper>
        </StyledForm>
    );
};
